import { formatDate } from '@angular/common';
import {
  AppointmentForm,
  AppointmentRecurrence,
  AppointmentRecurrencePattern,
  AppointmentRepeat,
  AppointmentRepeatRule,
  OutlookCalendarEvent,
  OutlookCalendarEventRecurrence,
  RecurrenceText,
  RecurrenceTranslate,
  SaveAppointmentResult,
  SeriesSaveProgress,
} from '@maximizer/outlook/shared/domain';
import { parseDateOnlyLocal } from '@maximizer/shared/util';
import { spansMultipleCalendarDays } from './appointment.util';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInCalendarDays,
  isAfter,
  isBefore,
  isWeekend,
  max,
  min,
  set,
  startOfDay,
} from 'date-fns';

export const MAX_RECURRENCE_RANGE_MONTHS = 12;
export const MAX_RECURRENCE_OCCURRENCES = 105;
// A daily series over the full range needs every day as a raw candidate before weekends are dropped.
const MAX_RECURRENCE_EXPANSION_STEPS = 400;
const RECURRENCE_LIMIT_PARAMS = {
  months: String(MAX_RECURRENCE_RANGE_MONTHS),
  max: String(MAX_RECURRENCE_OCCURRENCES),
};
export const RECURRENCE_DATE_FORMAT = 'dd MMMM yyyy';

const RECURRENCE_API_SET = '1.7';
export const DEFAULT_RECURRENCE_INTERVAL = 1;
export const MAX_RECURRENCE_INTERVAL = 99;
const DAYS_PER_WEEK = 7;

const OFFICE_DAY_INDEX: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const NO_RECURRENCE: OutlookCalendarEventRecurrence = {
  recurrence: null,
  hasUnsupportedRecurrence: false,
};

const UNSUPPORTED_RECURRENCE: OutlookCalendarEventRecurrence = {
  recurrence: null,
  hasUnsupportedRecurrence: true,
};

// Recurrence arrives with Mailbox 1.7; the add-in manifests declare 1.5.
export function supportsAppointmentRecurrence(): boolean {
  return (
    Office.context.requirements?.isSetSupported(
      'Mailbox',
      RECURRENCE_API_SET,
    ) ?? false
  );
}

export function maxRecurrenceEndsOn(startsOn: Date): Date {
  return addMonths(startOfDay(startsOn), MAX_RECURRENCE_RANGE_MONTHS);
}

export function clampRecurrenceEndsOn(
  startsOn: Date,
  endsOn: Date | null,
): Date {
  const earliest = startOfDay(startsOn);
  const latest = maxRecurrenceEndsOn(startsOn);
  if (!endsOn) return latest;
  return max([earliest, min([startOfDay(endsOn), latest])]);
}

// The occurrence cap wins over the range limit, whichever is reached first.
export function capRecurrenceEndsOn(recurrence: AppointmentRecurrence): Date {
  const dates = expandRecurrenceDates(recurrence);
  return dates.length > MAX_RECURRENCE_OCCURRENCES
    ? dates[MAX_RECURRENCE_OCCURRENCES - 1]
    : recurrence.endsOn;
}

export function defaultRecurrenceEndsOn(
  startsOn: Date,
  repeat: AppointmentRepeat,
): Date {
  return capRecurrenceEndsOn({
    repeat,
    interval: DEFAULT_RECURRENCE_INTERVAL,
    skipWeekends: false,
    startsOn,
    endsOn: maxRecurrenceEndsOn(startsOn),
  });
}

export function isRecurrenceEndsOnInRange(
  startsOn: Date,
  endsOn: Date,
): boolean {
  const day = startOfDay(endsOn);
  return (
    !isBefore(day, startOfDay(startsOn)) &&
    !isAfter(day, maxRecurrenceEndsOn(startsOn))
  );
}

function repeatRule(
  recurrence: Office.Recurrence,
  seriesStart: Date,
): AppointmentRepeatRule | null {
  const properties = recurrence.recurrenceProperties;

  switch (recurrence.recurrenceType) {
    case Office.MailboxEnums.RecurrenceType.Daily:
      return { repeat: 'daily', skipWeekends: false };
    case Office.MailboxEnums.RecurrenceType.Weekday:
      return { repeat: 'daily', skipWeekends: true };
    case Office.MailboxEnums.RecurrenceType.Weekly:
      return isWeeklyOnStartDay(properties, seriesStart)
        ? { repeat: 'weekly', skipWeekends: false }
        : null;
    case Office.MailboxEnums.RecurrenceType.Monthly:
      return isMonthlyOnStartDate(properties, seriesStart)
        ? { repeat: 'monthly', skipWeekends: false }
        : null;
    case Office.MailboxEnums.RecurrenceType.Yearly:
      return isYearlyOnStartDate(properties)
        ? { repeat: 'yearly', skipWeekends: false }
        : null;
    default:
      return null;
  }
}

function isWeeklyOnStartDay(
  properties: Office.RecurrenceProperties | undefined,
  seriesStart: Date,
): boolean {
  const days = properties?.days ?? [];
  if (days.length !== 1) return false;
  return (
    OFFICE_DAY_INDEX[String(days[0]).toLowerCase()] === seriesStart.getDay()
  );
}

function isMonthlyOnStartDate(
  properties: Office.RecurrenceProperties | undefined,
  seriesStart: Date,
): boolean {
  return (
    !properties?.weekNumber && properties?.dayOfMonth === seriesStart.getDate()
  );
}

function isYearlyOnStartDate(
  properties: Office.RecurrenceProperties | undefined,
): boolean {
  return (
    !properties?.weekNumber &&
    properties?.dayOfMonth !== undefined &&
    properties?.month !== undefined
  );
}

// A moved exception no longer sits on the pattern (its weekday, or its week for "every N weeks");
// expanding from it would shift every later row.
function isOccurrenceOnPattern(
  pattern: AppointmentRecurrencePattern,
  seriesStart: Date,
  occurrenceStart: Date,
): boolean {
  switch (pattern.repeat) {
    case 'daily':
      return !pattern.skipWeekends || !isWeekend(occurrenceStart);
    case 'weekly':
      return (
        differenceInCalendarDays(occurrenceStart, seriesStart) %
          (DAYS_PER_WEEK * pattern.interval) ===
        0
      );
    case 'monthly':
      return occurrenceStart.getDate() === seriesStart.getDate();
    case 'yearly':
      return (
        occurrenceStart.getDate() === seriesStart.getDate() &&
        occurrenceStart.getMonth() === seriesStart.getMonth()
      );
  }
}

function seriesStartsOn(
  recurrence: Office.Recurrence,
  occurrenceStart: Date,
): Date {
  return (
    parseDateOnlyLocal(recurrence.seriesTime?.getStartDate()) ??
    startOfDay(occurrenceStart)
  );
}

function seriesEndsOn(recurrence: Office.Recurrence): Date | null {
  return parseDateOnlyLocal(recurrence.seriesTime?.getEndDate());
}

export function isRecurrenceTruncated(
  recurrence: AppointmentRecurrence,
): boolean {
  if (recurrence.seriesEndsOn === undefined) return false;
  if (recurrence.seriesEndsOn === null) return true;
  return isAfter(
    startOfDay(recurrence.seriesEndsOn),
    startOfDay(recurrence.endsOn),
  );
}

export function exceedsRecurrenceOccurrences(dates: Date[]): boolean {
  return dates.length > MAX_RECURRENCE_OCCURRENCES;
}

// Maximizer stores an interval for weekly patterns only (core CRM offers "every N weeks").
export function supportsRecurrenceInterval(repeat: AppointmentRepeat): boolean {
  return repeat === 'weekly';
}

// The form control can hold null or a fraction while the user is typing.
export function isValidRecurrenceInterval(interval: number): boolean {
  return (
    Number.isInteger(interval) &&
    interval >= DEFAULT_RECURRENCE_INTERVAL &&
    interval <= MAX_RECURRENCE_INTERVAL
  );
}

function isIntervalStorable(
  repeat: AppointmentRepeat,
  interval: number,
): boolean {
  return (
    isValidRecurrenceInterval(interval) &&
    (interval === DEFAULT_RECURRENCE_INTERVAL ||
      supportsRecurrenceInterval(repeat))
  );
}

// The pattern is validated against the Outlook series start, but the saved series
// begins at the opened occurrence: earlier occurrences are not saved.
// Maximizer refuses recurring appointments that span more than one day.
export function mapOfficeRecurrence(
  recurrence: Office.Recurrence | null | undefined,
  eventStart: Date,
  eventEnd: Date,
): OutlookCalendarEventRecurrence {
  if (!recurrence) return NO_RECURRENCE;
  if (spansMultipleCalendarDays(eventStart, eventEnd)) {
    return UNSUPPORTED_RECURRENCE;
  }

  const seriesStart = seriesStartsOn(recurrence, eventStart);
  const repeat = repeatRule(recurrence, seriesStart);
  if (!repeat) return UNSUPPORTED_RECURRENCE;

  const interval =
    recurrence.recurrenceProperties?.interval ?? DEFAULT_RECURRENCE_INTERVAL;
  if (!isIntervalStorable(repeat.repeat, interval)) {
    return UNSUPPORTED_RECURRENCE;
  }

  const startsOn = startOfDay(eventStart);
  if (!isOccurrenceOnPattern({ ...repeat, interval }, seriesStart, startsOn)) {
    return UNSUPPORTED_RECURRENCE;
  }

  const seriesEnd = seriesEndsOn(recurrence);
  const withinRange: AppointmentRecurrence = {
    ...repeat,
    interval,
    startsOn,
    endsOn: clampRecurrenceEndsOn(startsOn, seriesEnd),
    seriesEndsOn: seriesEnd,
  };
  return {
    recurrence: { ...withinRange, endsOn: capRecurrenceEndsOn(withinRange) },
    hasUnsupportedRecurrence: false,
  };
}

const ADD_PERIOD: Record<
  AppointmentRepeat,
  (date: Date, amount: number) => Date
> = {
  daily: addDays,
  weekly: addWeeks,
  monthly: addMonths,
  yearly: addYears,
};

// Each occurrence is offset from the original start (never from the previous
// occurrence) so month-end clamping and DST never accumulate. An interval of
// 0 or null would never advance the candidate, so it yields no occurrences.
export function expandRecurrenceDates(
  recurrence: AppointmentRecurrence,
): Date[] {
  if (!isValidRecurrenceInterval(recurrence.interval)) return [];

  const startsOn = startOfDay(recurrence.startsOn);
  const endsOn = startOfDay(recurrence.endsOn);
  const addPeriod = ADD_PERIOD[recurrence.repeat];
  const dates: Date[] = [];

  for (
    let candidate = startsOn, index = 1;
    !isAfter(candidate, endsOn) && index <= MAX_RECURRENCE_EXPANSION_STEPS;
    candidate = addPeriod(startsOn, index++ * recurrence.interval)
  ) {
    dates.push(candidate);
  }

  return recurrence.skipWeekends
    ? dates.filter((date) => !isWeekend(date))
    : dates;
}

export function withTimeOfDay(day: Date, source: Date): Date {
  return set(day, {
    hours: source.getHours(),
    minutes: source.getMinutes(),
    seconds: source.getSeconds(),
    milliseconds: source.getMilliseconds(),
  });
}

export function shiftCalendarEventToDay(
  event: OutlookCalendarEvent,
  day: Date,
): OutlookCalendarEvent {
  const spanInDays = differenceInCalendarDays(event.end, event.start);
  return {
    ...event,
    start: withTimeOfDay(day, event.start),
    end: withTimeOfDay(addDays(day, spanInDays), event.end),
  };
}

export function shiftAppointmentFormToDay(
  form: AppointmentForm,
  day: Date,
): AppointmentForm {
  return { ...form, date: day };
}

export function formatRecurrenceDate(date: Date, localeId: string): string {
  return formatDate(date, RECURRENCE_DATE_FORMAT, localeId);
}

export function recurrenceText(
  translate: RecurrenceTranslate,
  localeId: string,
): RecurrenceText {
  return {
    translate,
    formatDate: (date) => formatRecurrenceDate(date, localeId),
  };
}

export function describeRecurrence(
  recurrence: AppointmentRecurrence,
  { translate, formatDate: formatDay }: RecurrenceText,
): string {
  const parts = [
    recurrence.interval > DEFAULT_RECURRENCE_INTERVAL
      ? translate(`outlook.appointment.recurrence.every.${recurrence.repeat}`, {
          count: String(recurrence.interval),
        })
      : translate(`outlook.appointment.recurrence.${recurrence.repeat}`),
  ];
  if (recurrence.skipWeekends) {
    parts.push(translate('outlook.appointment.recurrence.skipWeekends'));
  }
  parts.push(
    translate('outlook.appointment.recurrence.summaryEndsOn', {
      date: formatDay(recurrence.endsOn),
    }),
  );
  return parts.join(', ');
}

export function seriesProgressParams(progress: SeriesSaveProgress): {
  saved: number;
  total: number;
} {
  return { saved: progress.savedCount, total: progress.totalCount };
}

export function describeSaveAppointmentResult(
  result: SaveAppointmentResult,
  { translate, formatDate: formatDay }: RecurrenceText,
): string {
  if (result.hasUnsupportedRecurrence) {
    return translate(
      'outlook.calendar.saveAppointment.successUnsupportedRecurrence',
    );
  }
  if (result.savedThrough) {
    return translate('outlook.calendar.saveAppointment.successTruncated', {
      date: formatDay(result.savedThrough),
      ...RECURRENCE_LIMIT_PARAMS,
    });
  }
  return translate('outlook.calendar.saveAppointment.success');
}

export function describeRecurrenceTruncation(
  recurrence: AppointmentRecurrence,
  { translate, formatDate: formatDay }: RecurrenceText,
): string | null {
  if (!isRecurrenceTruncated(recurrence)) return null;

  const params = {
    savedThrough: formatDay(recurrence.endsOn),
    ...RECURRENCE_LIMIT_PARAMS,
  };
  return recurrence.seriesEndsOn
    ? translate('outlook.appointment.recurrence.truncated', {
        ...params,
        seriesEnd: formatDay(recurrence.seriesEndsOn),
      })
    : translate('outlook.appointment.recurrence.truncatedNoEnd', params);
}
